import React, { useState } from "react";
import type { ActivityViewData, JoinRequestMeta } from "../types";
import type { ActivityItemOptions } from "../ui/types";
import { ActivityItemShell } from "../ui/ActivityItemShell";
import Button from "../../Button";
import { RoleSelector } from "../../RoleSelector/RoleSelector";
import { useAcceptJoinRequestMutation } from "../mutations/useAcceptJoinRequestMutation";
import { useDenyJoinRequestMutation } from "../mutations/useDenyJoinRequestMutation";

/** Renders a workspace join request with role selector + deny button via mutation hooks. */
export function JoinRequestItem({
    activity,
    itemOptions
}: {
    activity: ActivityViewData;
    itemOptions?: ActivityItemOptions;
}) {
    const meta = (activity.meta as JoinRequestMeta | undefined) ?? {
        workspaceId: "",
        requestId: ""
    };
    const [selectedRole, setSelectedRole] = useState("");
    const acceptMutation = useAcceptJoinRequestMutation(meta);
    const denyMutation = useDenyJoinRequestMutation(meta);

    if (!meta.workspaceId) return null;

    const isPending = acceptMutation.isPending || denyMutation.isPending;

    return (
        <ActivityItemShell
            title={activity.title}
            icon={activity.icon}
            showUnreadDot={!!itemOptions?.onMarkRead && !activity.read}
            onMarkRead={itemOptions?.onMarkRead}
            timestamp={activity.createdAt}
            alwaysShowActions
            action={
                isPending ? (
                    <i className="far fa-spinner fa-spin text-sm text-brand" />
                ) : (
                    <div className="flex gap-1">
                        <RoleSelector
                            title="Accept as"
                            autoSetTitle
                            value={selectedRole}
                            menuButtonSize="xxsmall"
                            usePortal
                            onChange={(role) => {
                                setSelectedRole(role);
                                acceptMutation.mutate(role);
                            }}
                        />
                        <Button
                            type="secondary"
                            size="xxsmall"
                            onClick={() => denyMutation.mutate()}
                        >
                            Deny
                        </Button>
                    </div>
                )
            }
        >
            {activity.description && <span>{activity.description}</span>}
        </ActivityItemShell>
    );
}
